// frontend/src/hooks/useRecordDoor.ts
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { canvasserApi } from '../lib/api'
import { getPlacementFlips, localDateKey, markStopDoneHere, queueVisit, savePlacementFlip, setLocalVisitStatus } from '../lib/offlineStore'
import { heldBehindFlip } from '../lib/flipOrder'
import { syncEngine } from '../lib/syncEngine'
import { VisitActionFlag } from '../types/script'
import type { BatchVisitResult, RecordVisitRequest } from '../types/canvasser'
import type { FlipRequest } from '../lib/doorDraft'

export interface DoorSubmission {
  visits: RecordVisitRequest[]
  flips: FlipRequest[]
  /** Worksite stops: write today's Done-here marker (§15.1 S4). */
  doneHere: { turfId: string; stopKey: string } | null
}
export interface DoorSubmitResult { saved: number; queued: number; rejected: { contactId: string; message: string }[] }

/** The batch endpoint answers an ALL-rejected batch with 400 {message, results}: still per-item results. */
function resultsFromRejection(err: unknown): BatchVisitResult[] | null {
  const res = (err as { response?: { status?: number; data?: { results?: unknown } } } | null)?.response
  return res?.status === 400 && Array.isArray(res.data?.results) ? (res.data!.results as BatchVisitResult[]) : null
}

async function queue(v: RecordVisitRequest) {
  await queueVisit(v)
  await setLocalVisitStatus(v.contactId, v.actionFlags?.includes(VisitActionFlag.ComeBackLater) ?? false, v.turfId)
}

/**
 * [Spec §4.9 / §6.4, Phase 4 P4-6] Records one door: ONE batch call (per-item results). Saved items are
 * done; terminally rejected items are reported with the server's own message and never queued; anything
 * retryable (item failure, network, 5xx) is queued with its ORIGINAL ids for the sync engine. Then:
 * "Visit at home today" flips are stored (never for a rejected person) and drained (the drain waits for
 * a flip's visit to leave the queue, P4-S3), and a worksite's Done-here marker is written.
 * [Final-review I-3] A visit for someone whose "Visit at home today" flip from an EARLIER door hasn't
 * synced yet is queued, not sent: the server stamps WorksiteId from the current placement, so it must
 * follow the flip, and the sync engine orders that. This door's own NotAtWork visit (its flip is created
 * below) is sent as usual.
 */
export async function submitDoor(s: DoorSubmission): Promise<DoorSubmitResult> {
  const out: DoorSubmitResult = { saved: 0, queued: 0, rejected: [] }
  let heldCount = 0
  if (s.visits.length > 0) {
    let results: BatchVisitResult[] | null = null
    const online = navigator.onLine
    const held = online ? heldBehindFlip(await getPlacementFlips()) : () => false
    const sendable = s.visits.filter(v => !held(v))
    heldCount = s.visits.length - sendable.length
    if (online && sendable.length > 0) {
      try {
        results = (await canvasserApi.batchRecordVisits({ visits: sendable })).data.results
      } catch (err) {
        // Network error / 5xx: no per-item results, so everything is retryable and queued below.
        results = resultsFromRejection(err)
      }
    }
    const toQueue: RecordVisitRequest[] = []
    let i = 0
    s.visits.forEach(visit => {
      if (held(visit)) { toQueue.push(visit); return }
      const r = results?.[i++]
      if (r?.success) out.saved++
      else if (r?.rejected) out.rejected.push({ contactId: visit.contactId, message: r.error ?? 'This visit could not be saved' })
      else toQueue.push(visit)
    })
    for (const visit of toQueue) await queue(visit)
    out.queued = toQueue.length
    if (out.queued > 0) await syncEngine.refreshPendingCount()
  }

  const rejected = new Set(out.rejected.map(r => r.contactId))
  const flips = s.flips.filter(f => !rejected.has(f.contactId))
  for (const f of flips) await savePlacementFlip({ ...f, localDate: localDateKey(), synced: false, retryCount: 0 })
  if (flips.length > 0 && navigator.onLine) {
    try { await syncEngine.drainFlips() } catch { /* stays queued; the next sync drains it */ }
  }
  if (s.doneHere) await markStopDoneHere(s.doneHere.turfId, s.doneHere.stopKey)
  // Held-back visits go out right behind their flip (the engine drains flips, then releases them).
  if (heldCount > 0 && navigator.onLine) void syncEngine.sync().catch(() => {})
  return out
}

export function doorToast(r: DoorSubmitResult, noun = 'visit'): { type: 'success' | 'info' | 'error'; title: string; description?: string } {
  if (r.rejected.length > 0) {
    const n = r.rejected.length
    return { type: 'error', title: `${n} ${noun}${n === 1 ? '' : 's'} couldn't be saved`, description: r.rejected[0].message }
  }
  if (r.queued > 0) return { type: 'info', title: 'Saved on this phone, will sync' }
  if (r.saved > 0) return { type: 'success', title: 'Saved' }
  return { type: 'success', title: 'Done here' }
}

export function useRecordDoor() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: submitDoor,
    // The function handles offline itself (queue); TanStack's default 'online' would PAUSE it offline.
    networkMode: 'always',
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
      qc.invalidateQueries({ queryKey: ['canvasser', 'doneHere'] })
    },
  })
}
